import './LockPageButton.scss';

import { useAurigaContext } from '@context/AurigaContext.tsx';
import { useDeckContext } from '@context/DeckContext.tsx';
import { PermissionLevel, useUserContext } from '@context/UserContext.tsx';
import LockClock from '@mui/icons-material/LockClock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { Box, Button, Tooltip } from '@mui/material';
import { Page } from '@services/api/deckManagement/graphql/schema/API';
import { useLockPage, useReleasePageLock } from '@services/api/deckManagement/hooks/usePageLock';
import { formatTimestampToLocal } from '@utils/dateHelpers';
import { useEffect } from 'react';

/**
 * Props for LockPageButton.
 * @param deckId - Deck the page belongs to.
 * @param pageName - Logical page name (locks all grain variants together).
 * @param currentPage - The current page read (source of per-page lock state:
 *   locked_by / locked_at / lock_expiry).
 */
export interface LockPageButtonProps {
  deckId?: string;
  pageName?: string;
  currentPage?: Page | null;
  /** Optional callback invoked after the page lock is successfully released. */
  onReleaseSuccess?: () => void;
}

/**
 * Lock/Release Page button for an individual page.
 *
 * It locks all grain variants of a logical page (deck_id + page_name)
 *
 * A page cannot be locked while its deck is locked (mutual exclusion is enforced by the
 * backend); when the deck is locked, this button is disabled.
 */
const LockPageButton = ({ deckId, pageName, currentPage, onReleaseSuccess }: LockPageButtonProps) => {
  const { username, hasPermissionLevel } = useUserContext();
  const { deckLockStatus } = useDeckContext();
  const { toastNotification, showAlert, dismissAlert } = useAurigaContext();

  const isDevTeamAdmin = hasPermissionLevel(PermissionLevel.DEV_TEAM_ADMIN);

  // Per-page lock state, read from the page record.
  const lockedBy = currentPage?.locked_by ?? undefined;
  const lockedAt = currentPage?.locked_at ?? undefined;
  const lockExpiry = currentPage?.lock_expiry ?? undefined;
  const isLocked = Boolean(lockedBy);
  const isLockedByCurrentUser = isLocked && lockedBy === username;
  const isLockedByOtherUser = isLocked && !isLockedByCurrentUser;
  const canBreakGlass = isDevTeamAdmin && isLockedByOtherUser;

  // A page can't be locked while its deck is locked (mutual exclusion).
  const isDeckLocked = deckLockStatus.isLocked;

  // Show a system alert while THIS page is locked
  useEffect(() => {
    if (!isLocked || !lockedBy) {
      return undefined;
    }

    const formattedLockedAt = lockedAt ? formatTimestampToLocal(lockedAt, 'h:mm A') : '';
    const formattedExpiry = lockExpiry ? formatTimestampToLocal(lockExpiry, 'h:mm A') : '';

    // Scope the banner to THIS page's full route (deck + page)
    const pageRoute = `/decks/${deckId}/pages/${encodeURIComponent(pageName ?? '')}`;
    const alertId = showAlert({
      message: `Page ${pageName} is locked by ${lockedBy} at ${formattedLockedAt}. The lock will be automatically released at ${formattedExpiry}`,
      type: 'warning' as const,
      variant: 'standard' as const,
      visible: true,
      alertRoutes: [pageRoute],
      dismissible: false,
      icon: <LockClock />,
    });

    return () => {
      if (alertId) dismissAlert(alertId);
    };
  }, [isLocked, lockedBy, lockedAt, lockExpiry, pageName, deckId, showAlert, dismissAlert]);

  const lockPageMutation = useLockPage({
    onSuccess: (data) => {
      const formattedLockedAt = data.locked_at ? formatTimestampToLocal(data.locked_at, 'M/D/YYYY, h:mm:ss A') : '';
      toastNotification({ message: `Page Locked: ${formattedLockedAt}`, type: 'success', visible: true });
    },
    onError: (error) => {
      toastNotification({ message: `Failed to lock page: ${error.message}`, type: 'error', visible: true });
    },
  });

  const releasePageMutation = useReleasePageLock({
    onSuccess: () => {
      toastNotification({ message: 'Page released successfully', type: 'success', visible: true });
      onReleaseSuccess?.();
    },
    onError: (error) => {
      toastNotification({ message: `Failed to release page: ${error.message}`, type: 'error', visible: true });
    },
  });

  const handleClick = () => {
    if (!deckId || !pageName) {
      return;
    }
    // Do nothing if locked by another user
    if (isLockedByOtherUser && !canBreakGlass) {
      return;
    }

    if (isLocked && (isLockedByCurrentUser || canBreakGlass)) {
      releasePageMutation.mutate({ deckId, pageName });
    } else if (!isLocked) {
      lockPageMutation.mutate({ deckId, pageName });
    }
  };

  const isLoading = lockPageMutation.isPending || releasePageMutation.isPending;
  // Disabled when: loading, no page context, deck is locked (mutual exclusion), or the
  // page is locked by someone else
  const isDisabled = isLoading || !deckId || !pageName || isDeckLocked || (isLockedByOtherUser && !canBreakGlass);

  let tooltipText = 'Lock page for editing';
  if (isDeckLocked) {
    tooltipText = 'Cannot lock page when the deck is locked';
  } else if (isLockedByOtherUser && canBreakGlass) {
    tooltipText = `Break-glass unlock (Admin override) - Locked by ${lockedBy}`;
  } else if (isLockedByOtherUser) {
    tooltipText = `Locked by ${lockedBy}`;
  } else if (isLocked) {
    tooltipText = 'Release page lock';
  }

  let buttonText = 'Lock Page';
  if (isLoading) {
    buttonText = isLocked ? 'Releasing...' : 'Locking...';
  } else if (isLocked) {
    buttonText = 'Release Page';
  }

  return (
    <Tooltip title={tooltipText} arrow>
      <Box className='lock-page-button'>
        <Button
          variant={isLocked ? 'outlined' : 'contained'}
          color='secondary'
          size='medium'
          startIcon={isLocked ? <LockOpenIcon /> : <LockClock />}
          disabled={isDisabled}
          onClick={handleClick}
          fullWidth
        >
          {buttonText}
        </Button>
      </Box>
    </Tooltip>
  );
};

export default LockPageButton;
